import React, { useState, useEffect } from 'react';
import './StagingGate.css';
import logo from '../logo.png';

// Add meta tags to prevent crawling when staging gate is active
const addStagingMetaTags = () => {
  // Remove existing staging meta tags if any
  const existingNoIndex = document.querySelector('meta[name="robots"]');
  if (existingNoIndex) {
    existingNoIndex.remove();
  }

  // Add noindex, nofollow meta tag
  const metaRobots = document.createElement('meta');
  metaRobots.name = 'robots';
  metaRobots.content = 'noindex, nofollow, noarchive, nosnippet';
  document.head.appendChild(metaRobots);

  // Add X-Robots-Tag via meta (some crawlers respect this)
  const metaXRobots = document.createElement('meta');
  metaXRobots.httpEquiv = 'X-Robots-Tag';
  metaXRobots.content = 'noindex, nofollow';
  document.head.appendChild(metaXRobots);
};

const removeStagingMetaTags = () => {
  const metaRobots = document.querySelector('meta[name="robots"]');
  if (metaRobots && metaRobots.getAttribute('content')?.includes('noindex')) {
    metaRobots.remove();
  }
  const metaXRobots = document.querySelector('meta[http-equiv="X-Robots-Tag"]');
  if (metaXRobots) {
    metaXRobots.remove();
  }
};

const STAGING_PASSWORD = 'ylmjsogbyvjwvhpwyokp';
const STAGING_AUTH_KEY = 'staging_auth_verified';

const StagingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated for staging
    const checkAuth = () => {
      const authVerified = sessionStorage.getItem(STAGING_AUTH_KEY);
      if (authVerified === 'true') {
        setIsAuthenticated(true);
        // Remove meta tags when authenticated (user can access)
        removeStagingMetaTags();
      } else {
        // Add meta tags to prevent crawling when not authenticated
        addStagingMetaTags();
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    // Update meta tags when authentication state changes
    if (isAuthenticated) {
      removeStagingMetaTags();
    } else {
      addStagingMetaTags();
    }
  }, [isAuthenticated]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password === STAGING_PASSWORD) {
      // Store authentication in sessionStorage (clears on browser close)
      sessionStorage.setItem(STAGING_AUTH_KEY, 'true');
      setIsAuthenticated(true);
      // Clear password field
      setPassword('');
    } else {
      setError('Incorrect password. Access denied.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STAGING_AUTH_KEY);
    setIsAuthenticated(false);
    setPassword('');
    setError(null);
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="staging-gate-container">
        <div className="staging-gate-content">
          <div style={{ color: '#D6FF3A', textAlign: 'center' }}>Loading...</div>
        </div>
      </div>
    );
  }

  // Show password form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="staging-gate-container">
        <div className="staging-gate-content">
          <div className="staging-gate-logo">
            <img src={logo} alt="Fandom ID" />
          </div>
          <h2 className="staging-gate-title">Staging Environment</h2>
          <p className="staging-gate-subtitle">This is a protected staging environment. Please enter the password to continue.</p>
          <form onSubmit={handleSubmit} className="staging-gate-form">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="staging-gate-input"
              autoFocus
              required
            />
            {error && <div className="staging-gate-error">{error}</div>}
            <button type="submit" className="staging-gate-button">
              Access Staging
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Render protected content with logout option
  return (
    <>
      <div className="staging-gate-banner">
        <div className="staging-gate-banner-content">
          <span className="staging-gate-banner-text">⚠️ STAGING ENVIRONMENT</span>
          <button onClick={handleLogout} className="staging-gate-logout-btn">
            Logout
          </button>
        </div>
      </div>
      {children}
    </>
  );
};

export default StagingGate;

